import { useState, useEffect, useCallback, useRef } from 'react';
import { firebaseCacheService, FirebaseCacheOptions, FirebaseCachedData } from '../services/firebaseCacheService';

export interface UseFirebaseDataOptions extends Omit<FirebaseCacheOptions, 'collection' | 'docId'> {
  collection: string;
  docId: string;
  enabled?: boolean;
}

export interface UseFirebaseDataReturn<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
  isFromCache: boolean;
  isOffline: boolean;
  refresh: () => Promise<void>;
  updateData: (data: T) => Promise<void>;
  clearCache: () => Promise<void>;
  lastUpdated: Date | null;
}

export function useFirebaseData<T extends Record<string, any>>(
  options: UseFirebaseDataOptions
): UseFirebaseDataReturn<T> {
  const {
    collection,
    docId,
    maxAge = 600, // 10 minutes default
    enableRealtimeUpdates = true,
    fallbackToCache = true,
    enabled = true,
  } = options;

  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [isFromCache, setIsFromCache] = useState<boolean>(false);
  const [isOffline, setIsOffline] = useState<boolean>(
    typeof window !== 'undefined' ? !navigator.onLine : false
  );
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const mountedRef = useRef(true);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  // Monitor online/offline status
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleDataUpdate = useCallback(
    (result: FirebaseCachedData<T> | null, updateError?: Error) => {
      if (!mountedRef.current) return;

      if (updateError) {
        setError(updateError);
        setLoading(false);
        return;
      }

      if (result) {
        setData(result.data);
        setIsFromCache(result.fromCache);
        setLastUpdated(new Date(result.timestamp));
        setError(null);
      } else {
        setData(null);
        setIsFromCache(false);
      }

      setLoading(false);
    },
    []
  );

  // Set up data subscription
  useEffect(() => {
    if (!enabled || !mountedRef.current) return;

    setLoading(true);
    setError(null);

    console.log(`[USE_FIREBASE_DATA] Setting up subscription for ${collection}/${docId}`);

    // Cleanup previous subscription
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
    }

    // Set up new subscription
    const unsubscribe = firebaseCacheService.subscribeToDocument<T>(
      {
        collection,
        docId,
        maxAge,
        enableRealtimeUpdates,
        fallbackToCache,
      },
      handleDataUpdate
    );

    unsubscribeRef.current = unsubscribe;

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [collection, docId, maxAge, enableRealtimeUpdates, fallbackToCache, enabled, handleDataUpdate]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, []);

  const refresh = useCallback(async () => {
    if (!enabled) return;

    setLoading(true);
    setError(null);

    try {
      const result = await firebaseCacheService.getDocument<T>({
        collection,
        docId,
        maxAge: 0, // Force refresh
        fallbackToCache,
      });

      handleDataUpdate(result);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      handleDataUpdate(null, error);
    }
  }, [collection, docId, fallbackToCache, enabled, handleDataUpdate]);

  const updateData = useCallback(
    async (newData: T) => {
      if (!enabled) return;

      try {
        await firebaseCacheService.setDocument(collection, docId, newData);
        // Data will be updated via the real-time listener
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Unknown error');
        setError(error);
        throw error;
      }
    },
    [collection, docId, enabled]
  );

  const clearCache = useCallback(async () => {
    try {
      await firebaseCacheService.clearDocumentCache(collection, docId);
      if (enabled) {
        await refresh();
      }
    } catch (err) {
      console.error(`Failed to clear cache for ${collection}/${docId}:`, err);
    }
  }, [collection, docId, enabled, refresh]);

  return {
    data,
    loading,
    error,
    isFromCache,
    isOffline,
    refresh,
    updateData,
    clearCache,
    lastUpdated,
  };
}

// Hook for managing Firebase authentication state with caching
export function useFirebaseAuth() {
  const { data: userData, ...rest } = useFirebaseData({
    collection: 'users',
    docId: 'current-user', // This would be replaced with actual user ID
    maxAge: 300, // 5 minutes
    enableRealtimeUpdates: true,
    enabled: false, // Will be enabled when we have a user ID
  });

  return {
    userData,
    ...rest,
  };
}

// Hook for getting Firebase cache information
export function useFirebaseCacheManager() {
  const [cacheInfo, setCacheInfo] = useState<{ keys: string[]; size: number }>({
    keys: [],
    size: 0,
  });
  const [isOffline, setIsOffline] = useState<boolean>(
    typeof window !== 'undefined' ? !navigator.onLine : false
  );

  // Monitor online/offline status
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const refreshCacheInfo = useCallback(async () => {
    try {
      const info = await firebaseCacheService.getFirebaseCacheInfo();
      setCacheInfo(info);
    } catch (error) {
      console.error('Failed to get Firebase cache info:', error);
    }
  }, []);

  const clearAllCache = useCallback(async () => {
    try {
      await firebaseCacheService.clearAllFirebaseCache();
      await refreshCacheInfo();
    } catch (error) {
      console.error('Failed to clear all Firebase cache:', error);
    }
  }, [refreshCacheInfo]);

  useEffect(() => {
    refreshCacheInfo();
  }, [refreshCacheInfo]);

  return {
    cacheInfo,
    refreshCacheInfo,
    clearAllCache,
    isOffline,
  };
} 